import { defineStore } from 'pinia';
import { ref } from 'vue';
import client from '@/api/client';

export interface AuthUser {
  id: string;
  username: string;
  email: string;
  emailVerified: boolean;
  /** True when email is the `steam_<id>@placeholder.local` stub from Steam-only signup. */
  emailIsPlaceholder: boolean;
  role: string;
  steamId: string | null;
  avatarUrl: string | null;
  tradeUrl: string | null;
  walletAvailableCents: number;
  walletHeldCents: number;
  /** True once a full billing address is on file — required to top up the wallet. */
  billingComplete: boolean;
  createdAt: string;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const loading = ref(false);
  const initialized = ref(false);

  async function fetchUser(): Promise<void> {
    loading.value = true;
    try {
      const { data } = await client.get<AuthUser>('/users/me');
      user.value = data;
    } catch {
      user.value = null;
    } finally {
      loading.value = false;
      initialized.value = true;
    }
  }

  async function logout(): Promise<void> {
    try {
      await client.post('/auth/logout');
    } catch {
      // ignore errors — we clear state regardless
    } finally {
      user.value = null;
    }
  }

  function isAuthenticated(): boolean {
    return user.value !== null;
  }

  return { user, loading, initialized, fetchUser, logout, isAuthenticated };
});
